// src/pages/OnboardingHRPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Logo from '../components/Logo';
import PasswordField from '../components/forms/PasswordField';

// zentral konfigurierte Axios-Instanzen (wie bei Mentee/Mentor)
import { authApi, mentoringApi } from '../axiosInstance';

/* ======================================
   Utils
====================================== */

// SHA-256-Hash für Consent-Protokollierung
async function hashConsentText(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// Cookie-Fallback für CSRF, wenn Route nicht erreichbar
function readCookie(name) {
  const hit = document.cookie
    .split(';')
    .map(c => c.trim())
    .find(c => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.split('=')[1]) : '';
}

// HashRouter-kompatibel "?lang=" / "?token=" / "?package=" aus location.hash ziehen
function fromHash(name) {
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  const qs = hash.split('?')[1] || '';
  const p = new URLSearchParams(qs);
  return p.get(name);
}

/** Customer-ID robust aus verschiedenen whoami-Shapes lesen */
function extractCustomerId(who) {
  if (!who) return null;
  const src = who.data ?? who.user ?? who.profile ?? who.hr_profile ?? who;
  const ids = [
    src.customer_id, src.customerId, src.tenant_id, src.tenantId,
    src.customer?.id, src.tenant?.id,
  ];
  for (const v of ids) {
    if (v != null && `${v}` !== '' && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return null;
}

/* -------- Helper: ensureCustomerId (mehrere Fallback-Routen) -------- */
async function ensureCustomerId({ authApi, mentoringApi, csrf, i18n, company, email }) {
  // ✅ Anpassung #2: Keine Calls, wenn company leer ist (verhindert "missing_name")
  if (!company || !company.trim()) return null;

  // 1) whoami prüfen
  try {
    const whoRes = await authApi.get('/csrf-token', {
      withCredentials: true,
      headers: { 'Accept-Language': i18n.language },
    });
    let cid = extractCustomerId(whoRes?.data);
    if (cid) return cid;
  } catch { /* ignore */ }

  // 2) Kandidaten-Routen zum Anlegen auf Login-Server (3000) und Mentoring (3006)
  const candidates = [
    { client: authApi,      path: '/api/customers',                 body: { name: company, contactEmail: email } },
    { client: authApi,      path: '/customers',                     body: { name: company, contactEmail: email } },
    { client: mentoringApi, path: '/mentoring/customers',           body: { name: company, contact_email: email } },
    { client: mentoringApi, path: '/mentoring/customer',            body: { name: company, contact_email: email } },
    { client: authApi,      path: '/api/customers/ensure',          body: { name: company, contactEmail: email } },
    { client: mentoringApi, path: '/mentoring/customers/ensure',    body: { name: company, contact_email: email } },
  ];

  for (const c of candidates) {
    try {
      const res = await c.client.post(
        c.path,
        c.body,
        {
          withCredentials: true,
          headers: {
            'X-CSRF-Token': csrf,
            'Accept-Language': i18n.language,
            'X-Requested-With': 'XMLHttpRequest',
          },
          timeout: 6000,
        }
      );
      const d = res?.data ?? {};
      const cid = d.id ?? d.customer_id ?? d.data?.id ?? d.data?.customer_id ?? null;
      if (cid) return Number(cid);
    } catch (err) {
      const status = err?.response?.status;
      if (status >= 500 && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[HR Onboarding] customer create server error', c.path, status);
      }
    }
  }

  // 3) ggf. nochmals whoami – evtl. hat /onboarding-hr/profile bereits angelegt
  try {
    const whoRes2 = await authApi.get('/csrf-token', { withCredentials: true });
    const cid2 = extractCustomerId(whoRes2?.data);
    if (cid2) return cid2;
  } catch { /* ignore */ }

  return null; // kein Blocker
}

/* ======================================
   Component
====================================== */

export default function OnboardingHRPage({ csrfToken }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [company,   setCompany]   = useState('');
  const [companyLocked, setCompanyLocked] = useState(false); // Nur sperren, wenn Server Firmennamen liefert

  // gebuchtes Paket (aus URL/Invite), read-only anzeigen & mitsenden
  const [bookedPackage, setBookedPackage] = useState('');

  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Live-Passwortprüfung (vom PasswordField)
  const [passwordPolicy, setPasswordPolicy]   = useState(null);
  const [passwordValid,  setPasswordValid]    = useState(undefined); // undefined bis erstes Feedback kommt
  const [passwordIssues, setPasswordIssues]   = useState([]);

  const [contractConsent, setContractConsent] = useState(false);
  const [analysisConsent, setAnalysisConsent] = useState(false);

  const [invitationToken, setInvitationToken] = useState(''); // HR-Invite/Registration-Token (optional)
  const [submitting, setSubmitting] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [error, setError] = useState('');

  // NEU: Session-Rolle & zugeordnete Kunden-ID auslesen
  const [sessionRole, setSessionRole] = useState(null);
  const [sessionCustomerId, setSessionCustomerId] = useState(null);
  const [whoLoaded, setWhoLoaded] = useState(false);

  /* ======================================
     Sprache & Header
  ====================================== */
  useEffect(() => {
    const urlLang = searchParams.get('lang') || fromHash('lang');
    const saved   = localStorage.getItem('language') || localStorage.getItem('preferred_lang');

    const norm = (v) => (v && typeof v === 'string' ? v.toLowerCase().slice(0, 2) : null);
    const desired = norm(urlLang) || norm(saved);

    // ✅ Anpassung #1: richtige Sprachprüfung
    if (desired === 'de' || desired === 'en') {
      if (i18n.language !== desired) i18n.changeLanguage(desired);
      try {
        localStorage.setItem('language', desired);
        localStorage.setItem('preferred_lang', desired);
      } catch {}
    }
  }, [searchParams, i18n]);

  useEffect(() => {
    authApi.defaults.headers.common['Accept-Language'] = i18n.language;
    mentoringApi.defaults.headers.common['Accept-Language'] = i18n.language;
    try {
      localStorage.setItem('language', i18n.language);
      localStorage.setItem('preferred_lang', i18n.language);
    } catch {}
  }, [i18n.language]);

  /* ======================================
     Policy laden (mit Fallback-Route)
  ====================================== */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await authApi.get('/auth/password/policy', { withCredentials: true });
        if (mounted) setPasswordPolicy(r.data || null);
      } catch {
        try {
          const r2 = await authApi.get('/auth/password-policy', { withCredentials: true });
          if (mounted) setPasswordPolicy(r2.data || null);
        } catch {
          if (mounted) setPasswordPolicy(null);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  /* ======================================
     Session laden (wer bin ich?)
  ====================================== */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await authApi.get('/auth/whoami', { withCredentials: true });
        const u = r?.data?.user || {};
        if (!mounted) return;
        setSessionRole(u?.role || null);
        const cid = u?.customer_id ?? u?.tenant_id ?? null;
        setSessionCustomerId(cid ?? null);
      } catch {
        // ignore
      } finally {
        if (mounted) setWhoLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  /* ======================================
     Invite/URL: Token & Paket auslesen
  ====================================== */
  useEffect(() => {
    // 1) Token aus URL/Hash – bei Registrierung ist es i. d. R. in /#/register?... vorhanden.
    const tokenFromUrl = searchParams.get('token') || fromHash('token') || '';
    if (tokenFromUrl) {
      setInvitationToken(tokenFromUrl);
      try { localStorage.setItem('hr_invite_token', tokenFromUrl); } catch {}
    } else {
      // evtl. wurde der Token schon auf der Register-Seite gespeichert
      const tokenFromStore = localStorage.getItem('hr_invite_token') || '';
      if (tokenFromStore) setInvitationToken(tokenFromStore);
    }

    // 2) Paket aus URL/Hash (mehrere Synonyme unterstützen)
    const pkgFromUrl =
      searchParams.get('package') ||
      searchParams.get('pkg') ||
      searchParams.get('plan') ||
      fromHash('package') ||
      fromHash('pkg') ||
      fromHash('plan') ||
      '';

    if (pkgFromUrl) {
      setBookedPackage(pkgFromUrl);
      try { localStorage.setItem('hr_invite_package', pkgFromUrl); } catch {}
    } else {
      // wenn nicht in URL, evtl. zuvor gespeichert (z. B. von /register)
      const pkgStored = localStorage.getItem('hr_invite_package') || '';
      if (pkgStored) setBookedPackage(pkgStored);
    }
  }, [searchParams]);

  /* ======================================
     HR-Invite-Token => Unternehmensname / evtl. Paket
     (nur für HR-TEAM-Einladungen relevant; wenn Route existiert)
  ====================================== */
  useEffect(() => {
    if (!invitationToken) return;

    mentoringApi
      .get(`/mentoring/invite/hr-team/${encodeURIComponent(invitationToken)}`, { withCredentials: true })
      .then((res) => {
        if (res?.data?.customer_name) {
          setCompany(res.data.customer_name);
          setCompanyLocked(true); // nur sperren, wenn Server den Namen vorgibt
        }

        // falls die Invite-API das Paket mitliefert, übernehmen
        const pkg = res?.data?.package || res?.data?.plan || res?.data?.pkg;
        if (pkg) {
          setBookedPackage(pkg);
          try { localStorage.setItem('hr_invite_package', pkg); } catch {}
        }
      })
      .catch(() => {
        // kein harter Fehler – User kann Namen manuell eintragen (falls Feld sichtbar)
      });
  }, [invitationToken]);

  /* ======================================
     Consent-Texte (Hash-Protokoll)
  ====================================== */
  const contractConsentText = t('onboardingHR.contractConsentLong', t('onboardingMentee.contractConsentLong'));
  const analysisConsentText = t('onboardingHR.analysisConsentLong', t('onboardingMentee.analysisConsentLong'));
  const consentVersion = 'v1.0';

  const openPrivacyModal = (e) => { e.preventDefault(); setShowPrivacyModal(true); };
  const closePrivacyModal = () => setShowPrivacyModal(false);

  const passwordsMatch =
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword;

  /* ======================================
     CSRF laden (Route + Fallbacks)
  ====================================== */
  async function getCsrf() {
    try {
      const r = await authApi.get('/csrf-token', { withCredentials: true });
      return r?.data?.csrfToken || '';
    } catch {
      return (
        csrfToken ||
        readCookie('_csrf') ||
        readCookie('XSRF-TOKEN') ||
        readCookie('CSRF-TOKEN') ||
        readCookie('csrfToken') ||
        ''
      );
    }
  }

  /* ======================================
     Sichtbarkeit/Validierung der Firmenfelder
     (NEU: Felder ausblenden, wenn HR-Invite bereits Kunde zugeordnet)
  ====================================== */
  const urlRole = (searchParams.get('role') || fromHash('role') || '').toLowerCase();
  const roleGuess = (sessionRole || urlRole || '').toLowerCase();
  const isHr = roleGuess === 'hr_user' || roleGuess === 'hr_admin';

  // Sichtbar nur, wenn nicht HR mit schon bekannter customer_id
  const showCompanyFields = !(isHr && sessionCustomerId != null);

  /* ======================================
     Submit
  ====================================== */
  async function handleFinishOnboarding(e) {
    e.preventDefault();
    setError('');

    // Fallback: ist passwordValid noch undefined, dann min. 12 Zeichen fordern
    const passValidEffective =
      passwordValid === true || (passwordValid === undefined && newPassword.length >= 12);

    if (!firstName || !lastName || (showCompanyFields && !company)) {
      setError(t('onboardingHR.allFieldsRequired', 'Alle Felder sind erforderlich.'));
      return;
    }
    if (!passValidEffective) {
      setError(t('onboardingHR.passwordInvalid', 'Bitte ein sicheres Passwort wählen.'));
      return;
    }
    if (!passwordsMatch) {
      setError(t('onboardingHR.passwordsNoMatch', 'Passwörter stimmen nicht überein.'));
      return;
    }
    if (!contractConsent || !analysisConsent) {
      setError(t('onboardingHR.consentRequired', 'Bitte beide Einwilligungen bestätigen.'));
      return;
    }

    setSubmitting(true);
    try {
      const csrf = await getCsrf();

      // Token/Paket ggf. aus localStorage nachladen (falls URL ohne Param war)
      const tokenToSend =
        invitationToken ||
        localStorage.getItem('hr_invite_token') ||
        undefined;

      const packageToSend =
        bookedPackage ||
        localStorage.getItem('hr_invite_package') ||
        undefined;

      // 1) Profil auf dem AUTH-Server speichern
      //    -> Firmenfelder nur mitschicken, wenn sie sichtbar/gewünscht sind
      const profilePayload = {
        firstName: firstName,
        lastName:  lastName,
        token:     tokenToSend,               // optional
      };
      if (showCompanyFields && company) profilePayload.company = company;
      if (showCompanyFields && packageToSend) profilePayload.packageName = packageToSend;

      await authApi.patch(
        '/profile',
        profilePayload,
        {
          withCredentials: true,
          headers: {
            'X-CSRF-Token': csrf,
            'Accept-Language': i18n.language,
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      // 2) Passwort setzen (AUTH-Server; akzeptiert password / newPassword)
      await authApi.post(
        '/onboarding-hr/password',
        { newPassword: newPassword },
        {
          withCredentials: true,
          headers: {
            'X-CSRF-Token': csrf,
            'Accept-Language': i18n.language,
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      // 3) „Best-Effort“-Consents am Mentoring-Server (3006).
      try {
        const [contractHash, analysisHash] = await Promise.all([
          hashConsentText(contractConsentText),
          hashConsentText(analysisConsentText),
        ]);

        await mentoringApi.post(
          '/mentoring/consent',
          {
            consent_type: 'contract_participation',
            consent_status: 'granted',
            consent_version: consentVersion,
            consent_text_hash: contractHash,
            language: i18n.language,
          },
          { withCredentials: true, headers: { 'X-CSRF-Token': csrf } }
        );

        await mentoringApi.post(
          '/mentoring/consent',
          {
            consent_type: 'mentor_match_analysis',
            consent_status: 'granted',
            consent_version: consentVersion,
            consent_text_hash: analysisHash,
            language: i18n.language,
          },
          { withCredentials: true, headers: { 'X-CSRF-Token': csrf } }
        );
      } catch (cErr) {
        const status = cErr?.response?.status;
        if (status !== 403 && status !== 401 && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[HR Onboarding] consent not recorded', cErr?.message);
        }
      }

      // 3.5) Customer automatisch / robust anlegen, falls noch keiner existiert
      try {
        // E-Mail aus whoami für contactEmail ableiten (optional)
        let email = '';
        try {
          const whoRes = await authApi.get('/csrf-token', { withCredentials: true });
          const w = whoRes?.data?.data ?? whoRes?.data ?? {};
          email = w?.user?.email || w?.profile?.email || w?.hr_profile?.email || '';
        } catch { /* ignore */ }

        await ensureCustomerId({
          authApi,
          mentoringApi,
          csrf,
          i18n,
          company,
          email,
        });
      } catch { /* kein Blocker */ }

      // 4) Weiter ins HR-Portal (Sprache mitgeben)
      navigate(`/hr-profile?lang=${i18n.language}`, { replace: true });
    } catch (err) {
      const serverText =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.error;
      setError(serverText || t('onboardingHR.loadingError', 'Fehler beim Onboarding. Bitte versuche es erneut.'));
    } finally {
      setSubmitting(false);
    }
  }

  /* ======================================
     Render
  ====================================== */

  const passValidEffective =
    passwordValid === true || (passwordValid === undefined && newPassword.length >= 12);

  const canSubmit =
    firstName &&
    lastName &&
    (!showCompanyFields || company) &&
    contractConsent &&
    analysisConsent &&
    passwordsMatch &&
    passValidEffective &&
    !submitting;

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-white">
        <p role="alert" className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-white">
      {/* Logo */}
      <div className="mb-10">
        <Logo variant="wordmark" size="xxxl" />
      </div>

      {/* Titel */}
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        {t('onboardingHR.heading', 'HR-Onboarding: Account vervollständigen')}
      </h2>

      {/* Intro */}
      <div className="bg-gray-800 p-4 rounded-2xl mb-8 w-full max-w-2xl text-white text-center">
        {t('onboardingHR.intro', 'Bitte vervollständige anschließend dein Profil und setze jetzt ein sicheres Passwort.')}
      </div>

      {/* Hinweis, falls Firmenfelder ausgeblendet werden */}
      {whoLoaded && !showCompanyFields && (
        <div className="bg-emerald-900/25 border border-emerald-700/40 text-emerald-100 rounded-2xl px-4 py-3 mb-6 w-full max-w-2xl">
          {t('onboardingHR.companyAlreadyAssigned', 'Dein Unternehmen ist über die Einladung bereits zugeordnet.')}
          {sessionCustomerId != null && (
            <> {t('onboardingHR.customerId', 'Kunden-ID')}: <b>{sessionCustomerId}</b></>
          )}
        </div>
      )}

      {/* Formular */}
      <form onSubmit={handleFinishOnboarding} className="bg-gray-800 p-8 rounded-2xl shadow-md w-full max-w-md">
        {/* Vorname */}
        <div className="mb-6">
          <label htmlFor="firstName" className="block mb-2 font-semibold">
            {t('onboardingHR.firstName', 'Vorname')}
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="w-full p-2 bg-gray-700 rounded text-white"
            autoComplete="given-name"
          />
        </div>

        {/* Nachname */}
        <div className="mb-6">
          <label htmlFor="lastName" className="block mb-2 font-semibold">
            {t('onboardingHR.lastName', 'Nachname')}
          </label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            className="w-full p-2 bg-gray-700 rounded text-white"
            autoComplete="family-name"
          />
        </div>

        {/* Unternehmen & Paket – NUR anzeigen, wenn erforderlich */}
        {showCompanyFields && (
          <>
            <div className="mb-6">
              <label htmlFor="company" className="block mb-2 font-semibold">
                {t('onboardingHR.company', 'Unternehmensname')}
              </label>
              <input
                id="company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
                className="w-full p-2 bg-gray-700 rounded text-white"
                readOnly={companyLocked}
              />
            </div>

            <div className="mb-6">
              <label htmlFor="bookedPackage" className="block mb-2 font-semibold">
                {t('onboardingHR.bookedPackage', 'Gebuchtes Paket')}
              </label>
              <input
                id="bookedPackage"
                type="text"
                value={bookedPackage || ''}
                onChange={() => {}}
                readOnly
                placeholder="—"
                className="w-full p-2 bg-gray-700 rounded text-white opacity-90 cursor-not-allowed"
              />
              <div className="text-xs text-gray-400 mt-1">
                {t('onboardingHR.packageHint', 'Kommt i. d. R. aus der Einladung oder URL (package=…).')}
              </div>
            </div>
          </>
        )}

        {/* Passwort mit Live-Check */}
        <div className="mb-6">
          <label htmlFor="newPassword" className="block mb-2 font-semibold">
            {t('onboardingHR.password', 'Passwort')}
          </label>
          <PasswordField
            value={newPassword}
            onChange={setNewPassword}
            policy={passwordPolicy}
            onValidityChange={(ok, issues) => {
              setPasswordValid(ok === true ? true : ok === false ? false : undefined);
              setPasswordIssues(Array.isArray(issues) ? issues : []);
            }}
          />
          {passwordIssues.length > 0 && (
            <ul className="mt-2 text-xs text-red-400 list-disc ml-5">
              {passwordIssues.map((it, idx) => <li key={idx}>{it}</li>)}
            </ul>
          )}
        </div>

        {/* Passwort bestätigen */}
        <div className="mb-6">
          <label htmlFor="confirmPassword" className="block mb-2 font-semibold">
            {t('onboardingHR.confirmPassword', 'Passwort bestätigen')}
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="w-full p-2 bg-gray-700 rounded text-white"
            autoComplete="new-password"
          />
          {confirmPassword.length > 0 && !passwordsMatch && (
            <div className="text-xs text-red-400 mt-1">
              {t('onboardingHR.passwordsNoMatch', 'Passwörter stimmen nicht überein.')}
            </div>
          )}
        </div>

        {/* Consent 1 */}
        <div className="mb-4">
          <label htmlFor="contractConsent" className="flex items-start space-x-3 cursor-pointer select-none">
            <input
              id="contractConsent"
              type="checkbox"
              checked={contractConsent}
              onChange={(e) => setContractConsent(e.target.checked)}
              className="mr-2 mt-1"
              required
            />
            <span>
              <Trans
                i18nKey="onboardingHR.contractConsent"
                defaults="Ich habe die Datenschutzinformationen mit der Beauftragung bestätigt und stimme der Teilnahme am Programm zu."
              />
            </span>
          </label>
          <div className="ml-6 mt-1 text-xs text-gray-300">
            {t('onboardingHR.contractConsentHint', 'Erforderlich für die Programmnutzung.')}
          </div>
        </div>

        {/* Consent 2 */}
        <div className="mb-4">
          <label htmlFor="analysisConsent" className="flex items-start space-x-3 cursor-pointer select-none">
            <input
              id="analysisConsent"
              type="checkbox"
              checked={analysisConsent}
              onChange={(e) => setAnalysisConsent(e.target.checked)}
              className="mr-2 mt-1"
              required
            />
            <span>{t('onboardingHR.analysisConsent', 'Ich habe die Nutzungsvereinbarung mit der Beauftragung zugestimmt.')}</span>
          </label>
        </div>

        <button
          type="submit"
          className={`w-full bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 rounded-2xl transition mt-2 ${
            !canSubmit ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          disabled={!canSubmit}
        >
          {submitting ? t('onboardingHR.submitting', 'Speichern …') : t('onboardingHR.finish', 'Speichern & starten')}
        </button>
      </form>
    </div>
  );
}
